// src/pages/AdminPanel/Dashboard.jsx
import React, { useEffect, useState } from 'react';
import { db } from '../../services/firebase'; // Ensure correct path
import { collection, getDocs } from 'firebase/firestore';
import { Link } from 'react-router-dom'; // For navigation
import { Package, ShoppingBag, Users, DollarSign, Ticket, Settings, PlusCircle, CreditCard, Gift, BarChart, PackageCheck, Wallet2 } from 'lucide-react'; // Icons
import "./Dashboard.css"

const Dashboard = () => {
  const [stats, setStats] = useState({
    products: 0,
    orders: 0,
    users: 0,
    admins: 0, 
    revenue: 0,
    tickets: 0,
    shipments: 0 // Renamed from pending to shipments for clarity
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchStats = async () => {
      setLoading(true);
      setError(null);
      try {
        const productSnap = await getDocs(collection(db, 'products'));
        const userSnap = await getDocs(collection(db, 'users'));
        const orderSnap = await getDocs(collection(db, 'orders'));
        const adminSnap = await getDocs(collection(db, 'admins'));
        const shipmentSnap = await getDocs(collection(db, 'shipments')); // Assuming this collection exists
        const ticketSnap = await getDocs(collection(db, 'tickets')); // Assuming this collection exists for tickets

        let totalRevenue = 0;
        orderSnap.forEach(doc => {
          const orderData = doc.data();
          // Assuming 'totalAmount' is stored in cents for orders
          totalRevenue += orderData.totalAmount || 0; 
        });

        setStats({
          products: productSnap.size,
          users: userSnap.size,
          orders: orderSnap.size,
          admins: adminSnap.size,
          revenue: totalRevenue / 100, // Convert cents to dollars for display
          tickets: ticketSnap.size,
          shipments: shipmentSnap.size // Use size for count of pending shipments
        });
      } catch (err) {
        console.error("Error fetching dashboard stats:", err);
        setError("Failed to load dashboard data. Please check console for details.");
      } finally {
        setLoading(false);
      }
    };

    fetchStats();
  }, []);

  if (loading) return <div className="text-center py-10 text-gray-300 text-xl">Loading Dashboard...</div>;
  if (error) return <div className="text-center py-10 text-red-400 text-xl">{error}</div>;

  const StatCard = ({ title, value, icon: Icon, link }) => (
    <article className="dashboard-stat-card"> {/* Custom class for specific styling */}
      <div className="card-icon-wrapper">
        {Icon && <Icon size={28} className="text-white" />}
      </div>
      <h3 className="card-title">{title}</h3>
      <p className="card-value">{value}</p>
      {link && (
        <Link to={link} className="card-link">
          View Details &rarr;
        </Link>
      )}
    </article>
  );

  return (
    <div className="dashboard-container"> {/* Uses main layout styling */}
      <header className="dashboard-header-main">
        <h1 className="text-4xl font-extrabold text-white uppercase tracking-wider">Admin Dashboard</h1>
      </header>

      <section className="dashboard-summary-cards">
        <StatCard title="Total Products" value={stats.products} icon={Package} link="/admin/products" />
        <StatCard title="Total Orders" value={stats.orders} icon={ShoppingBag} link="/admin/orders" />
        <StatCard title="Pending Shipments" value={stats.shipments} icon={CreditCard} link="/admin/orders" /> {/* Link to orders for pending */}
        <StatCard title="Registered Users" value={stats.users} icon={Users} link="/admin/customers" />
        <StatCard title="Total Revenue" value={`$${stats.revenue.toFixed(2)}`} icon={DollarSign} link="/admin/wallet"/>
        <StatCard title="Support Tickets" value={stats.tickets} icon={Ticket} link="/admin/tickets"/>
      </section>

      <section className="dashboard-quick-actions">
        <h2 className="section-title">Quick Actions</h2>
        <div className="actions-grid">
          <Link 
            to="/admin/products/add" 
            className="action-button primary"
          >
            <PlusCircle className="mr-2" size={24} /> Add New Product
          </Link>
          <Link 
            to="/admin/settings"
            className="action-button secondary"
          >
            <Settings className="mr-2" size={24} /> Manage Settings
          </Link>
          <button 
            className="action-button secondary"
            onClick={() => alert('Manage Discount Codes functionality will be built later!')}
          >
            <Gift className="mr-2" size={24} /> Manage Discount Codes
          </button>
          <Link 
            to="/admin/analytics"
            className="action-button secondary"
          >
            <BarChart className="mr-2" size={24} /> View Analytics
          </Link>
          <Link 
            to="/admin/wallet"
            className="action-button secondary"
          >
            <Wallet2 className="mr-2" size={24} /> My Wallet
          </Link>
          <Link 
            to="/admin/shipment-center"
            className="action-button secondary"
          >
            <PackageCheck className="mr-2" size={24} /> Shipment Center
          </Link>
        </div>
      </section>
    </div>
  );
};

export default Dashboard;
